import { Project } from "@/integrations/supabase/types/";
import { Card } from "@/components/ui/card";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { formatDistanceToNow } from "date-fns";
import { FileText, CheckCircle2, MessageSquare, DollarSign, Calendar } from "lucide-react";

interface ProjectActivityProps {
  project: Project;
}

export const ProjectActivity = ({ project }: ProjectActivityProps) => {
  // Fetch all activity data
  const { data: tasks } = useQuery({
    queryKey: ["project-tasks-activity", project.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("project_tasks")
        .select("*, created_by, updated_at")
        .eq("project_id", project.id)
        .order("updated_at", { ascending: false })
        .limit(10);
      if (error) throw error;
      return data;
    }
  });

  const { data: comments } = useQuery({
    queryKey: ["project-comments-activity", project.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("project_comments")
        .select("*")
        .eq("project_id", project.id)
        .order("created_at", { ascending: false })
        .limit(10);
      if (error) throw error;
      return data;
    }
  });

  const { data: expenses } = useQuery({
    queryKey: ["project-expenses-activity", project.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("project_expenses")
        .select("*")
        .eq("project_id", project.id)
        .order("created_at", { ascending: false })
        .limit(10);
      if (error) throw error;
      return data;
    }
  });

  const { data: milestones } = useQuery({
    queryKey: ["project-milestones-activity", project.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("project_milestones")
        .select("*")
        .eq("project_id", project.id)
        .order("updated_at", { ascending: false })
        .limit(10);
      if (error) throw error;
      return data;
    }
  });

  // Combine and sort all activities
  const activities = [
    ...(tasks?.map(t => ({
      type: "task",
      icon: CheckCircle2,
      title: `Task updated: ${t.title}`,
      description: `Status: ${t.status}`,
      timestamp: new Date(t.updated_at),
      color: "text-blue-500"
    })) || []),
    ...(comments?.map(c => ({
      type: "comment",
      icon: MessageSquare,
      title: "New comment added",
      description: c.comment.substring(0, 50) + (c.comment.length > 50 ? "..." : ""),
      timestamp: new Date(c.created_at),
      color: "text-green-500"
    })) || []),
    ...(expenses?.map(e => ({
      type: "expense",
      icon: DollarSign,
      title: "Expense recorded",
      description: `${e.description}: $${Number(e.amount).toLocaleString()}`,
      timestamp: new Date(e.created_at),
      color: "text-red-500"
    })) || []),
    ...(milestones?.map(m => ({
      type: "milestone",
      icon: Calendar,
      title: `Milestone: ${m.title}`,
      description: `Status: ${m.status}`,
      timestamp: new Date(m.updated_at),
      color: "text-purple-500"
    })) || [])
  ].sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold">Recent Activity</h3>
      
      <ScrollArea className="h-[500px] pr-4">
        <div className="space-y-4">
          {activities.length > 0 ? (
            activities.map((activity, index) => {
              const Icon = activity.icon;
              return (
                <Card key={index} className="glass-card p-4">
                  <div className="flex gap-3">
                    <div className={`p-2 rounded-lg bg-muted/50 h-fit`}>
                      <Icon className={`h-4 w-4 ${activity.color}`} />
                    </div>
                    <div className="flex-1">
                      <h4 className="font-medium">{activity.title}</h4>
                      <p className="text-sm text-muted-foreground mt-1">
                        {activity.description}
                      </p>
                      <p className="text-xs text-muted-foreground mt-2">
                        {formatDistanceToNow(activity.timestamp, { addSuffix: true })}
                      </p>
                    </div>
                  </div>
                </Card>
              );
            })
          ) : (
            <Card className="glass-card p-8 text-center">
              <p className="text-muted-foreground">No activity yet. Start working on the project to see activity here.</p>
            </Card>
          )}
        </div>
      </ScrollArea>
    </div>
  );
};
